/**
 * Table of UUIDs used across the application
 * This table helps track and document the purpose of various UUIDs used in the system
 */

export const UUIDs = {
	// Form IDs
	forms: {
		oxfordKnee: "e2f876c1-4b4c-4a9e-a338-b6d7b9eb1e1f",
		womac: "28e49946-4c5e-4bf0-b6ea-7b73803b3e27",
		eq5d5l: "c6b5cdc2-d3d8-44a8-8bd5-3db2696464bf",
		moxfq: "131c8bad-8458-438d-8017-5e03a373e5eb",
		heelpain: "c5e6069b-0d00-49a8-aa88-28428023faa2",
		faam: "f3c5e7b2-9a8d-4e7c-a1d6-6f3b9c8d7e9a",
	},

	// Pain Score Form IDs
	painScores: {
		painScore1: "20c05eda-61ba-4531-b3ca-5811ea7c6970",
		painScore2: "289abef6-3ce8-4b5b-b621-d622b5efc975",
		painScore3: "333d9f81-c0f8-4b28-a795-804bbdc0af78",
		painScore4: "404d9f81-c0f8-4b28-a795-804bbdc0af78",
		painScore5: "ad5ae755-bfaf-4797-bf10-71d61a5c7465",
		painScore6: "b1c23d4e-5f6a-7b8c-9d0e-1f2a3b4c5d6e",
		painScore7: "e4f56a7b-8c9d-0e1f-2a3b-4c5d6e7f8a9b",
	},

	// MOxFQ Page IDs
	moxfqPages: {
		painMobility: "20f741a4-b4b0-4960-b00e-0ce7b8dc6a69",
		dailyActivities: "30f741a4-b4b0-4960-b00e-0ce7b8dc6a70",
		painCharacteristics: "40f741a4-b4b0-4960-b00e-0ce7b8dc6a71",
		painAssessment: "50f741a4-b4b0-4960-b00e-0ce7b8dc6a72",
	},

	// MOxFQ Score Input IDs
	moxfqScores: {
		walkingStanding: "70f741a4-b4b0-4960-b00e-0ce7b8dc6a91",
		pain: "70f741a4-b4b0-4960-b00e-0ce7b8dc6a92",
		socialInteraction: "70f741a4-b4b0-4960-b00e-0ce7b8dc6a93",
		total: "70f741a4-b4b0-4960-b00e-0ce7b8dc6a94",
	},

	// Form Total Score IDs
	womacInputs: {
		// WOMAC Total Scores
		womacPain: "6b213817-9f43-4f97-a4a2-4c9224cd9a64",
		womacStiffness: "7386bddb-4ae3-46b1-bbdf-a1c416f3fa2e",
		womacFunction: "b63ede98-1a9a-4edd-9320-bea4441509fd",
		womacTotal: "2f7ac607-f650-4f50-9397-583725f98d0c",
	},
	oxfordKneeInputs: {
		// Oxford Knee Total Scores
		oxfordKneeTotal: "2ac66c73-1245-4540-a3c0-dfbe1f8783f2",
		oxfordKneeFunction: "11dec58c-7ce7-456a-a997-d2cf410ca37f",
		oxfordKneePain: "39029fed-98e8-45a9-867e-37dcd6de8108",
	},
	eq5dInputs: {
		// EQ-5D Total Score
		eq5dTotal: "68f17a5e-7e1b-4afe-a7f1-078d101c07a1",
	},
	heelpainInputs: {
		compliance: "ac3457bd-6aad-449e-9c31-0f2eac03e9d7",
	},
	faamInputs: {
		// Calculated Scores
		adlSubscale: "d70adf4a-189f-4eec-aea4-5a4f09a57dc2",
		sportsSubscale: "7b0f6529-1162-4b05-9dad-ea70350b8d98",
		totalScore: "1f96d131-c530-4567-8e79-e6a1b8c0f33e",
		// Important Questions
		adlRating: "d2e3f4a5-b6c7-4d8e-9f0a-1b2c3d4e5f6a",
		sportsRating: "a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5e",
		function: "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6f",
	},
} as const;

// Type for the UUIDs object
export type UUIDs = typeof UUIDs;

// Helper type to get all possible UUID values
export type UUIDValue = UUIDs[keyof UUIDs][keyof UUIDs[keyof UUIDs]];

// Helper function to check if a string is a valid UUID from our table
export function isValidUUID(uuid: string): uuid is UUIDValue {
	return Object.values(UUIDs).some((category) =>
		Object.values(category).includes(uuid as UUIDValue),
	);
}

// Helper function to get the category and name of a UUID
export function getUUIDInfo(
	uuid: string,
): { category: string; name: string } | null {
	for (const [category, values] of Object.entries(UUIDs)) {
		for (const [name, value] of Object.entries(values)) {
			if (value === uuid) {
				return { category, name };
			}
		}
	}
	return null;
}
